import React from 'react';
import axios from 'axios';

import type { CheckboxChangeEvent } from 'antd/lib/checkbox';
import errorReporter from '@app/errorReporter';
import type { NotificationType } from '@bb/domain/dist/types';
import { Thumbnail, Icon, message, Avatar } from 'components';

import { AuthContext } from 'contexts';
import { Icon as NewIcon } from '@bb/elements-web';
import type { NotificationSearchResponse } from '@/app-package';
import type { actions } from './actions';
import {
  SET_SEARCH_DELETED,
  SET_SEARCH_TERM,
  INCREMENT_PAGE,
  INCREMENT_SKIP,
  RESET_OFFSET,
} from './actions';

type Notification = NotificationSearchResponse['notifications'][number];

type SearchParamsType = {
  page: number;
  limit: number;
  skip: number;
  deleted: boolean;
  text?: string;
  time?: Date;
};

const MAX_NOTIFICATIONS = 20;

const initialFilters: Record<NotificationType, boolean> = {
  manualCoop: true,
  coop: true,
  agent: true,
  announcement: true,
  transfer: true,
  keyCoop: true,
};

const initialSearchParams = {
  page: 0,
  limit: MAX_NOTIFICATIONS,
  skip: 0,
  deleted: false,
};

const reducer = (state: SearchParamsType, action: actions) => {
  switch (action.type) {
    case SET_SEARCH_DELETED: {
      return { ...state, text: '', page: 0, skip: 0, deleted: action.payload };
    }
    case SET_SEARCH_TERM: {
      return { ...state, page: 0, text: action.payload.trim() };
    }
    case INCREMENT_PAGE: {
      return { ...state, page: state.page + 1 };
    }
    case INCREMENT_SKIP: {
      return { ...state, skip: state.skip + 1 };
    }
    case RESET_OFFSET:
      // keeping search text if any
      return { ...state, ...initialSearchParams, deleted: state.deleted };
    default:
      return state;
  }
};

const getQueryNotificationType = (filters: typeof initialFilters) => {
  const filterLength = Object.keys(initialFilters).length;
  if (Object.values(filters).filter(val => val).length === filterLength) {
    return ['all'];
  }
  return Object.keys(filters).filter(
    (filter: NotificationType) => filters[filter],
  );
};

const useNotification = () => {
  const { setPusherEvents } = React.useContext(AuthContext);
  const [notifications, setNotifications] = React.useState<Notification[]>([]);
  const [hasMore, setHasMore] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(false);
  const [countDirty, setCountDirty] = React.useState(0);
  const [filters, setFilters] = React.useState(initialFilters);
  const [searchParams, dispatch] = React.useReducer(
    reducer,
    initialSearchParams,
  );

  const markAllRead = async () => {
    try {
      await axios.post('/notification/markAllRead');
      const clearedNotification = notifications.map(notification => ({
        ...notification,
        isDirty: false,
      }));
      setNotifications(clearedNotification);
    } catch (err) {
      errorReporter.error(err);
      message.error(`Something went wrong. Please try again.`);
    }
  };

  const deleteRestoreNotification = async (
    op: 'delete' | 'undo',
    notificationId: string,
  ) => {
    const endpoint = op === 'delete' ? '/notification/delete' : '/notification';
    try {
      await axios.post(`${endpoint}/${notificationId}`);

      if (op === 'undo') {
        setNotifications(curNotifications =>
          curNotifications.filter(({ id }) => id !== notificationId),
        );
      }
      dispatch({ type: INCREMENT_SKIP });
    } catch (err) {
      const action = op === 'delete' ? 'delete' : 'restore';
      errorReporter.error(err);
      message.error(`Failed to ${action} the notification. Please try again.`);
    }
  };

  const getNotifications = async () => {
    if (isLoading) return;

    const loadMore = searchParams.page !== 0;
    if (!loadMore) {
      setIsLoading(true);
    }
    try {
      const { text, ...restSearchParams } = searchParams;
      const { data } = await axios.get<NotificationSearchResponse>(
        '/notification/search',
        {
          params: {
            ...(text === '' ? {} : { text }),
            ...restSearchParams,
            notificationType: getQueryNotificationType(filters),
          },
        },
      );

      const newNotificationList = loadMore
        ? [...notifications, ...data.notifications]
        : data.notifications;
      setCountDirty(data.countDirty);
      setHasMore(data.countTotal > newNotificationList.length);
      setNotifications(newNotificationList);
    } catch (err) {
      errorReporter.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const onLoadMore = () => {
    dispatch({ type: INCREMENT_PAGE });
  };

  const filtersCheckboxProps = (filter: keyof typeof filters) => ({
    checked: filters[filter],
    onChange: ({ target: { checked } }: CheckboxChangeEvent) =>
      setFilters({ ...filters, [filter]: checked }),
  });

  React.useEffect(() => {
    getNotifications();
  }, [searchParams.text, searchParams.page, searchParams.deleted]);

  React.useEffect(() => {
    setNotifications([]);
    dispatch({ type: RESET_OFFSET });
    setHasMore(true);
    getNotifications();
  }, [filters]);

  const getNotificationType = ({ type, notificationType }: Notification) => {
    if (
      type.includes('appointment') ||
      ['listingExpiryReminder', 'virtualTourConfirmed'].includes(type)
    ) {
      return notificationType === 'coop' ? 'appointment' : 'listing';
    }
    if (type.includes('offer')) return 'offer';
    if (type.includes('feedback')) return 'feedback';
    if (type.includes('transfer')) return 'transfer';
    if (type.includes('manualCoopAdded')) return 'manualCoopAdded';
    if (type.includes('manualCoopRemoved')) return 'manualCoopRemoved';
    return 'blast';
  };

  const getAvatarComponent = (type: string, { thumbnail }: Notification) => {
    const iconProps = { width: 40, color: 'primary' } as const;
    switch (type) {
      case 'listing':
        if (thumbnail) {
          return React.createElement(Thumbnail, {
            src: thumbnail,
            ...iconProps,
          });
        }
        return React.createElement(Icon, { type: 'home', ...iconProps });
      case 'blast':
        return React.createElement(Icon, { type: 'bullhorn', ...iconProps });
      case 'feedback':
        return React.createElement(Icon, {
          type: 'pencilSquare',
          ...iconProps,
        });
      case 'offer':
        return React.createElement(Icon, { type: 'fileText', ...iconProps });
      case 'appointment':
        return React.createElement(Icon, { type: 'calendar', ...iconProps });
      case 'transfer':
        if (thumbnail) {
          return React.createElement(Avatar, { src: thumbnail });
        }
        return React.createElement(Icon, {
          type: 'antdSwapOutlined',
          ...iconProps,
        });
      case 'manualCoopAdded':
        if (thumbnail) {
          return React.createElement(Thumbnail, {
            src: thumbnail,
            overlayIcon: React.createElement(NewIcon, {
              icon: 'userAddCircle',
              size: 28,
              color: 'primary',
            }),
            overlayIconSize: 28,
            color: 'primary',
          });
        }
        return React.createElement(NewIcon, {
          icon: 'userAddCircle',
          size: 40,
          color: 'primary',
        });
      case 'manualCoopRemoved':
        if (thumbnail) {
          return React.createElement(Thumbnail, {
            src: thumbnail,
            overlayIcon: React.createElement(NewIcon, {
              icon: 'userRemoveCircle',
              size: 28,
              color: 'danger',
            }),
            overlayIconSize: 28,
            color: 'danger',
          });
        }
        return React.createElement(NewIcon, {
          icon: 'userRemoveCircle',
          size: 40,
          color: 'danger',
        });
      default:
        return null;
    }
  };

  const socketNotificationDelete = (notificationId: string) => {
    let wasDirty = false;
    setNotifications(currNotifications =>
      currNotifications.filter(({ id, isDirty }) => {
        if (id === notificationId) wasDirty = isDirty;
        return id !== notificationId;
      }),
    );
    if (wasDirty) {
      setCountDirty(prev => prev - 1);
    }
  };

  const socketNotificationRead = (notificationId: string) => {
    let wasDirty = false;
    setNotifications(currNotifications =>
      currNotifications.map(notification => {
        if (notification.id === notificationId) {
          wasDirty = notification.isDirty;
          return { ...notification, isDirty: false };
        }
        return notification;
      }),
    );
    if (wasDirty) {
      setCountDirty(prev => prev - 1);
    }
  };

  const socketNotificationAllRead = () => {
    setNotifications(currNotifications =>
      currNotifications.map(notification => ({
        ...notification,
        isDirty: false,
      })),
    );
    setCountDirty(0);
  };

  const socketNotificationUpdate = async (notificationId: string) => {
    try {
      const { data: notification } = await axios.get<Notification>(
        `/notification/${notificationId}`,
      );
      if (notification) {
        setNotifications(currNotifications => [
          notification,
          ...currNotifications,
        ]);
        // Always isDirty true, since only triggered on save
        setCountDirty(prev => prev + 1);
      }
    } catch (err) {
      errorReporter.error(err);
    }
  };

  React.useEffect(() => {
    const toggleSockets = (isBind: boolean) => {
      setPusherEvents(
        'notification',
        'notificationRead',
        socketNotificationRead,
        isBind,
      );
      setPusherEvents(
        'notification',
        'notificationAllRead',
        socketNotificationAllRead,
        isBind,
      );
      setPusherEvents(
        'notification',
        'notificationDelete',
        socketNotificationDelete,
        isBind,
      );
      setPusherEvents(
        'notification',
        'notificationUpdate',
        socketNotificationUpdate,
        isBind,
      );
    };
    if (setPusherEvents) {
      toggleSockets(true);
      return () => toggleSockets(false);
    }
  }, [setPusherEvents]);

  return {
    notifications,
    isLoading,
    countDirty,
    filtersCheckboxProps,
    getNotificationType,
    getAvatarComponent,
    onLoadMore,
    dispatch,
    markAllRead,
    hasMore,
    deleteRestoreNotification,
  };
};
export default useNotification;
